// RITA-1525 — Wertemodi für die Seitenansicht und die PDF-Probe.
//
// „namen“ zeigt die Platzhalter als Marken {{KEY}}, „beispiel“ die Beispielwerte des Katalogs (Grundfall
// „Berater“; die weiteren Beispielfälle und die Marke der Vorlage reicht der Arbeitsbereich als
// `Platzhalterbelegung` aus `beispielfaelle.ts` herein, RITA-1597), „lang“ bewusst lange Werte für die
// Umbruchprobe und „leer“ fehlende Werte (bleiben leer, keine erfundenen Ersatzwerte — fachliche Regel aus
// RITA-901). Die langen Werte sind seit RITA-1597 eine stimmige Person: eine Beraterin mit sehr langem Namen,
// eine Firma mit sehr langem Namen als Vertragsinhaber (und damit Vertragspartner), eine lange Marke und eine
// andere Person als Bearbeiterin. ANREDE und GESCHLECHT bleiben echte Werte („Frau“, „Weiblich“ – geschrieben wie im
// echten Vertrag, siehe renderAdvisorTemplate), weil es im PRM nur diese Wörter gibt.
import { VERTRAG_BEISPIELWERTE, VertragPlatzhalterKey } from './platzhalterkatalog';

/**
 * RITA-1543: echte Werte eines Beraters für die Vorschau bei „Vertrag erstellen“. Fehlende oder
 * unbekannte Schlüssel bleiben leer. Läuft wie die anderen Modi durch Anzeige UND Seitenmessung
 * (`useSeitenaufteilung`), damit die Seiten so umbrechen, wie sie angezeigt werden.
 */
export interface Platzhalterbelegung {
  werte: Readonly<Partial<Record<string, string>>>;
}

/** Die im Vorlagen-Editor wählbaren Modi (ohne echte Werte). */
export type Anzeigemodus = 'namen' | 'beispiel' | 'lang' | 'leer';

export type Wertemodus = Anzeigemodus | Platzhalterbelegung;

export const WERTEMODI: ReadonlyArray<{ wert: Anzeigemodus; bezeichnung: string }> = [
  { wert: 'namen', bezeichnung: 'Platzhalternamen' },
  { wert: 'beispiel', bezeichnung: 'Beispielwerte' },
  { wert: 'lang', bezeichnung: 'Lange Werte' },
  { wert: 'leer', bezeichnung: 'Fehlende Werte' },
];

/** Feste Wortlisten des PRM: hier gibt es keinen „langen“ Wert, nur den echten (RITA-1597). */
export const LANGE_WERTE_FESTE_WOERTER: readonly VertragPlatzhalterKey[] = ['ANREDE', 'GESCHLECHT'];

const LANGE_INHABERIN =
  'Firma von Hohenstein Reise- und Touristikberatung GmbH & Co. KG, vertreten durch die Geschäftsführung';

export const LANGE_WERTE: Readonly<Partial<Record<VertragPlatzhalterKey, string>>> = {
  ANREDE: 'Frau',
  PERSOENLICHE_ANREDE: 'Liebe Maximiliane-Alexandra Konstanze Friederike',
  VORNAME: 'Maximiliane-Alexandra Konstanze Friederike',
  NACHNAME: 'von Hohenstein-Mustermann-Wiedemann zu Osterhausen',
  GESCHLECHT: 'Weiblich',
  GESCHAEFTSEMAIL: 'maximiliane-alexandra.von-hohenstein-mustermann@beispiel-reiseberatung-musterhausen.de',
  PRIVATEEMAIL: 'maximiliane-alexandra.konstanze.friederike.privat@beispiel-familie-von-hohenstein.de',
  MYJACK_LOGIN: 'maximiliane-alexandra.von-hohenstein-mustermann-wiedemann',
  STRASSE: 'Lange Ausfallstraße am Musterplatz 1234a, Hinterhaus, 3. Obergeschoss links',
  ORT: 'Neustadt an der Weinstraße-Musterhausen',
  LAND: 'Bundesrepublik Deutschland',
  FIRMA: 'Mein Urlaubsglück Reisevermittlung und Touristikservice GmbH & Co. KG, Niederlassung Norddeutschland',
  VERTRAGSPARTNER: LANGE_INHABERIN,
  VERTRAGSINHABER: LANGE_INHABERIN,
  BANKNAME: 'Sparkasse Musterstadt-Beispielhausen Zweigstelle Musterplatz',
  TEAMLEITER: 'Anna-Katharina Schmidt-Beispielmann',
  BEARBEITER: 'Friederike-Charlotte Beispielmann-Wiedemann von Osterhausen',
};

/**
 * Wert eines Platzhalters im gewählten Modus. `null` bedeutet: als Marke {{KEY}} darstellen.
 */
export function platzhalterwert(schluessel: VertragPlatzhalterKey, modus: Wertemodus): string | null {
  if (typeof modus === 'object') return modus.werte[schluessel] ?? '';
  switch (modus) {
    case 'namen':
      return null;
    case 'beispiel':
      return VERTRAG_BEISPIELWERTE[schluessel];
    case 'lang':
      return (
        LANGE_WERTE[schluessel] ??
        `${VERTRAG_BEISPIELWERTE[schluessel]} (sehr langer Beispielwert für die Umbruchprobe)`
      );
    case 'leer':
      return '';
    default:
      return null;
  }
}
